import { useEffect, useRef, useState } from 'react';

/**
 * Shared plot frame for the dashboard's charts.
 *
 * Charts here are hand-composed visx marks inside an `aria-hidden` SVG, with
 * focusable HTML overlays on top carrying the real labels. visx supplies scales and
 * shapes; the SVG, the CSS tokens and the accessible layer stay ours.
 */

export interface PlotPadding {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/**
 * Measure the element a chart draws into.
 *
 * A chart cannot render until it knows its width, so callers hold off on the SVG
 * until this reports a non-zero one.
 */
export function useContainerWidth(): {
  ref: React.RefObject<HTMLDivElement | null>;
  width: number;
} {
  const ref = useRef<HTMLDivElement | null>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new ResizeObserver((entries) => {
      const box = entries[0]?.contentRect;
      if (box) setWidth(Math.round(box.width));
    });
    observer.observe(node);
    setWidth(Math.round(node.getBoundingClientRect().width));
    return () => observer.disconnect();
  }, []);
  return { ref, width };
}

export interface PlotBox {
  width: number;
  height: number;
  padding: PlotPadding;
  /** Drawable area inside the padding. Never negative, so a squeezed panel still renders. */
  innerWidth: number;
  innerHeight: number;
}

export function plotBox(width: number, height: number, padding: PlotPadding): PlotBox {
  return {
    width,
    height,
    padding,
    innerWidth: Math.max(0, width - padding.left - padding.right),
    innerHeight: Math.max(0, height - padding.top - padding.bottom),
  };
}
